// Ported from hotwire-flow (MIT, @hotwirebits/flow) into conek-ui. The engine
// logic is upstream's; identifiers/events/classes follow the conek contract in
// docs/superpowers/specs/2026-09-09-flow-graph-components-design.md §2.

// Shared utility — get the flow controller from the DOM.
// Used by controllers that can't resolve outlets across subtrees.
export function getFlowController(application, canvasId = "flow-canvas") {
  try {
    const canvas = document.getElementById(canvasId)
    if (!canvas || !application) return null
    return application.getControllerForElementAndIdentifier(canvas, "ui--flow")
  } catch (e) { return null }
}

// Engine resolution for the satellite widget controllers (F10/m6): the
// Stimulus outlet when it is connected, else the legacy id-registry lookup
// keyed by the canvas id in the controller's OWN outlet attribute
// (`data-<identifier>-ui--flow-outlet="#<id>"` — every Flow::* widget
// component emits it), and only then the historical "flow-canvas" default.
// The old bare getFlowController(this.application) fallback always resolved
// the canvas with id "flow-canvas" — with a generated default canvas id
// (ui-flow-canvas-<uuid>) or two canvases on the page, widgets silently drove
// the wrong graph (or none at all).
export function resolveFlowEngine(controller) {
  // Outlet access can throw after teardown (Stimulus "Missing outlet
  // element"); fall through to the id lookup, then null.
  try { if (controller.hasUiFlowOutlet) return controller.uiFlowOutlet } catch (e) { }
  const selector = controller.element.getAttribute(`data-${controller.identifier}-ui--flow-outlet`)
  const id = selector && selector.startsWith("#") ? selector.slice(1) : null
  return getFlowController(controller.application, id || "flow-canvas")
}

// Declared handle type for the strict-mode connection gate: the Stimulus
// type value when the markup carries it, else the --target class convention
// (same fallback _readNodeHandles uses), else null — UNKNOWN. Callers treat
// null as permissive: a veto may only fire on a KNOWN mismatch, so legacy
// markup without type attributes never loses connections.
export function handleType(el) {
  return el.dataset["ui-FlowHandleTypeValue"] || (el.classList.contains("ui-flow-handle--target") ? "target" : null)
}

// Where a handle sits ALONG its side, as a 0..1 fraction (U9 multi-port).
// NodeComponent spreads handles that share a side at k/(n+1) and writes the
// fraction as data-handle-offset; a lone handle carries none and stays at the
// classic midpoint. Anything malformed or outside (0, 1) falls back to 0.5.
export function handleFraction(el) {
  const value = parseFloat(el?.dataset?.handleOffset)
  return Number.isFinite(value) && value > 0 && value < 1 ? value : 0.5
}

// Flow-space anchor of a handle on its node box: the side's edge, `fraction`
// of the way along it. Single source for edge paths, box-select and the
// connect-start position, so all three agree.
export function sideAnchor(position, x, y, w, h, fraction = 0.5) {
  switch (position) {
    case "top": return { x: x + w * fraction, y }
    case "bottom": return { x: x + w * fraction, y: y + h }
    case "left": return { x, y: y + h * fraction }
    default: return { x: x + w, y: y + h * fraction }
  }
}

// Drop-target resolution shared by the fresh-connect drop (handle_controller)
// and the reconnect drop (flow_controller's _onReconnectPointerUp): a direct
// hit on a [data-handle] wins; otherwise snap to the nearest handle whose
// rect is within flow.connectionRadiusValue screen px (client coords on both
// sides of the comparison — zoom-independent). excludeEl skips the gesture's
// own origin handle in the radius scan; validType, when given, restricts the
// scan to handles of that declared type (unknown-type handles stay eligible —
// the completion path's type gate decides their fate). accept, when given, is
// a predicate over handle ids (the host's validTargets set): a handle it
// rejects is excluded from the scan.
export function resolveDropTarget(flow, event, excludeEl = null, validType = null, accept = null) {
  const direct = event.target?.closest ? event.target.closest("[data-handle]") : null
  if (direct) return direct
  if (!flow || !flow.element) return null
  const declared = flow.connectionRadiusValue
  const radius = typeof declared === "number" && declared > 0 ? declared : 20
  const x = event.clientX, y = event.clientY
  let best = null, bestDist = radius
  flow.element.querySelectorAll("[data-handle]").forEach((el) => {
    if (el === excludeEl) return
    if (validType) { const t = handleType(el); if (t && t !== validType) return }
    if (accept && !accept(el.dataset.handle)) return
    const r = el.getBoundingClientRect()
    if (!r.width && !r.height) return // detached / jsdom zero-rect
    const dx = Math.max(r.left - x, 0, x - r.right)
    const dy = Math.max(r.top - y, 0, y - r.bottom)
    const d = Math.hypot(dx, dy)
    if (d < bestDist) { bestDist = d; best = el }
  })
  return best
}
